import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { syncServerTime, startAutoSync, getNow } from '../lib/serverTime';

// ─── Types ─────────────────────────────────────────────────────────────────
interface EventoSync {
  id: string;
  data_inicio: string;
  data_evento: string;
  status: string;
  current_item_id?: string | null;
  item_end_time?: string | null;
  override_mode?: boolean | null;
  override_until?: string | null;
}

interface EventSyncState {
  eventoAtivo: EventoSync | null;
  alvoAbsoluto: string | null;
  isModoTeste: boolean;
  configCarregada: boolean;
  realtimeConnected: boolean;
  refresh: () => void;
}

// ─── Context ───────────────────────────────────────────────────────────────
const EventSyncContext = createContext<EventSyncState>({
  eventoAtivo: null,
  alvoAbsoluto: null,
  isModoTeste: false,
  configCarregada: false,
  realtimeConnected: false,
  refresh: () => {},
});

export function useEventSync(): EventSyncState {
  return useContext(EventSyncContext);
}

// ─── Provider ──────────────────────────────────────────────────────────────
export function EventSyncProvider({ children }: { children: React.ReactNode }) {
  const [eventoAtivo, setEventoAtivo] = useState<EventoSync | null>(null);
  const [alvoAbsoluto, setAlvoAbsoluto] = useState<string | null>(null);
  const [isModoTeste, setIsModoTeste] = useState(false);
  const [configCarregada, setConfigCarregada] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  const alvoAbsolutoRef = useRef<string | null>(null);
  const syncAtTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const syncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncRunningRef = useRef(false);
  const syncPendingRef = useRef(false);

  useEffect(() => { alvoAbsolutoRef.current = alvoAbsoluto; }, [alvoAbsoluto]);

  // ─── Core sync logic — uses raw fetch with cache-busting ─────────────────
  const syncAllCore = useCallback(async () => {
    if (!isSupabaseConfigured) return;

    const SUPA_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    const SUPA_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
    if (!SUPA_URL || !SUPA_KEY) return;

    const cb = Date.now();
    const fetchHeaders = {
      'apikey': SUPA_KEY,
      'Authorization': `Bearer ${SUPA_KEY}`,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    };

    try {
      const [configResponse, eventoResponse] = await Promise.all([
        fetch(
          `${SUPA_URL}/rest/v1/configuracoes_publicas?id=eq.global&select=modo_teste_ativo,cronometro_alvo_absoluto,cronometro_sync_at&limit=1&_cb=${cb}`,
          { method: 'GET', cache: 'no-store', headers: fetchHeaders }
        ),
        fetch(
          `${SUPA_URL}/rest/v1/eventos?status=in.(em_preparacao,pre_disputa,ao_vivo,aguardando_pagamento)&is_test=neq.true&select=id,data_inicio,data_evento,status,current_item_id,item_end_time,override_mode,override_until&order=data_evento.asc&limit=1&_cb=${cb}`,
          { method: 'GET', cache: 'no-store', headers: fetchHeaders }
        ),
      ]);

      if (!configResponse.ok || !eventoResponse.ok) return;

      const cfgArr = await configResponse.json();
      const evtArr = await eventoResponse.json();

      const cfg = Array.isArray(cfgArr) ? cfgArr[0] : null;
      const evtData: EventoSync | null = Array.isArray(evtArr) ? (evtArr[0] ?? null) : null;

      setEventoAtivo(evtData);

      if (syncAtTimeoutRef.current) {
        clearTimeout(syncAtTimeoutRef.current);
        syncAtTimeoutRef.current = null;
      }

      // ── OVERRIDE CHECK: EventoPage reads override_until DIRECTLY from the event.
      // EventSyncContext does NOT set alvoAbsoluto to override_until anymore.
      // This prevents race conditions when restore is clicked:
      // - local 1s poll clears activeOverrideUntil immediately
      // - alvoAbsolutoCtx stays as next 10:00 AM (normal target)
      // - no transition fires accidentally
      const overrideUntilMs = evtData?.override_until
        ? new Date(evtData.override_until).getTime()
        : 0;
      const nowForOverride = Date.now() - 10_000;
      if (overrideUntilMs > nowForOverride) {
        console.log('[EventSync] Override detected — EventoPage handles it directly', {
          override_until: evtData!.override_until,
          evento_id: evtData!.id,
        });
        // Do NOT set isModoTeste(true) or targetMs = overrideUntilMs here.
        // EventoPage reads evento.override_until directly from 1s polling.
      }

      // Always calculate the NORMAL alvoAbsoluto (test mode or event data_inicio).
      // This is independent of override_until.
      let targetMs: number | null = null;
      const isTestMode = !!(cfg?.modo_teste_ativo && cfg?.cronometro_alvo_absoluto);
      setIsModoTeste(isTestMode);

      if (isTestMode && cfg?.cronometro_alvo_absoluto) {
        const parsed = new Date(cfg.cronometro_alvo_absoluto).getTime();
        if (!isNaN(parsed)) targetMs = parsed;
      } else if (evtData && evtData.status !== 'ao_vivo' && evtData.status !== 'aguardando_pagamento' && evtData.status !== 'finalizado') {
        // ✅ FIX CRÍTICO: Usar data_inicio DO EVENTO em vez de calcular próxima 10:00 BRT.
        // E NUNCA setar alvoAbsoluto para eventos ao_vivo/finalizado:
        //   - ao_vivo: o alvo (data_inicio) já passou — se setarmos, o trigger de transição
        //     no EventoPage fica chamando executarTransicao INFINITAMENTE todo segundo no desktop
        //   - aguardando_pagamento/finalizado: evento encerrado, não precisa de countdown
        //
        // Usando data_inicio do evento, o target permanece fixo (hoje 10:00 BRT)
        // mesmo depois que o horário passa — garantindo que o cronômetro não resete.
        if (evtData.data_inicio) {
          const parsed = new Date(evtData.data_inicio).getTime();
          if (!isNaN(parsed)) targetMs = parsed;
        }
        // Fallback: só calcular próxima 10:00 BRT se data_inicio não existir
        if (!targetMs) {
          const agora = Date.now();
          const proximaDez = new Date(agora);
          proximaDez.setUTCHours(13, 0, 0, 0); // 10:00 BRT
          if (proximaDez.getTime() <= agora) {
            proximaDez.setUTCDate(proximaDez.getUTCDate() + 1);
          }
          targetMs = proximaDez.getTime();
        }
      }

      if (targetMs !== null) {
        const utcIso = new Date(targetMs).toISOString();

        if (isTestMode && cfg?.cronometro_sync_at) {
          const syncAtMs = new Date(cfg.cronometro_sync_at).getTime();
          const delay = Math.max(0, syncAtMs - getNow());

          if (delay > 50) {
            setAlvoAbsoluto(null);
            alvoAbsolutoRef.current = null;
            syncAtTimeoutRef.current = setTimeout(() => {
              setAlvoAbsoluto(utcIso);
              alvoAbsolutoRef.current = utcIso;
              syncAtTimeoutRef.current = null;
              console.log('[EventSync] alvoAbsoluto activated at sync_at:', utcIso);
            }, delay);
          } else {
            setAlvoAbsoluto(utcIso);
            alvoAbsolutoRef.current = utcIso;
          }
        } else {
          setAlvoAbsoluto(utcIso);
          alvoAbsolutoRef.current = utcIso;
        }
      } else {
        setAlvoAbsoluto(null);
        alvoAbsolutoRef.current = null;
      }
    } catch (e) {
      console.warn('[EventSync] syncAll failed:', e);
    } finally {
      setConfigCarregada(true);
    }
  }, []);

  const syncAll = useCallback(() => {
    if (syncDebounceRef.current) {
      clearTimeout(syncDebounceRef.current);
      syncDebounceRef.current = null;
    }

    const run = async () => {
      if (syncRunningRef.current) {
        syncPendingRef.current = true;
        return;
      }
      syncRunningRef.current = true;
      syncPendingRef.current = false;

      await syncAllCore();

      syncRunningRef.current = false;

      if (syncPendingRef.current) {
        syncPendingRef.current = false;
        // No delay — run immediately so mobile gets latest data ASAP
        run();
      }
    };

    // Run immediately — no 50ms debounce delay
    run();
  }, [syncAllCore]);

  // ─── Initial sync ────────────────────────────────────────────────────────
  useEffect(() => {
    syncAll();
  }, [syncAll]);

  // ─── Supabase Realtime — GLOBAL subscription ────────────────────────────
  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) return;

    // ── Channel factory — creates and subscribes a new channel ──────────────
    // Using a ref so the reconnect logic can always access the latest channel.
    const channelRef = { current: null as ReturnType<typeof supabase.channel> | null };

    const createChannel = () => {
      const ch = supabase
        .channel(`global-event-sync-v4-${Date.now()}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'eventos' },
          (payload: any) => {
            console.log('REALTIME EVENT RECEIVED', payload);

            const updated = payload.new as Partial<EventoSync>;

            if (updated && updated.id) {
              setEventoAtivo(prev => {
                if (!prev) {
                  if (updated.status && ['em_preparacao','pre_disputa','ao_vivo','aguardando_pagamento'].includes(updated.status)) {
                    return { ...updated } as EventoSync;
                  }
                  return prev;
                }
                if (prev.id !== updated.id) return prev;
                const merged = { ...prev, ...updated } as EventoSync;
                return merged;
              });
            }

            console.log('[EventSync] 🔔 eventos changed → syncing (REALTIME)');
            syncAll();
          }
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'configuracoes_publicas' },
          (payload: any) => {
            console.log('REALTIME CONFIG RECEIVED', payload);
            console.log('[EventSync] 🔔 configuracoes_sistema changed → syncing (REALTIME)');
            syncAll();
          }
        )
        .subscribe((status) => {
          const connected = status === 'SUBSCRIBED';
          setRealtimeConnected(connected);
          if (connected) {
            console.log('[EventSync] ✅ Global realtime channel SUBSCRIBED (v4)');
            syncAll();
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn('[EventSync] ⚠️ Realtime error/timeout — will reconnect. Status:', status);
            setRealtimeConnected(false);
          } else {
            console.log('[EventSync] ⚠️ Realtime status:', status);
          }
        });

      channelRef.current = ch;
      return ch;
    };

    createChannel();

    // ✅ FIX: Reconnect timer now CREATES A NEW CHANNEL instead of just polling.
    // Previously it called supabase.removeChannel(channel) + syncAll() but never
    // created a new subscription — so realtime was permanently lost after first disconnect.
    const reconnectTimer = setInterval(async () => {
      const ch = channelRef.current;
      const state = (ch as any)?.state ?? '';
      if (state === 'closed' || state === 'errored' || state === 'leaving') {
        console.warn('[EventSync] 🔁 Realtime channel dead — recreating...', state);
        if (ch) {
          try { await supabase.removeChannel(ch); } catch { /* silent */ }
        }
        // Create a brand new channel subscription
        createChannel();
        syncAll();
      }
    }, 15_000);

    return () => {
      clearInterval(reconnectTimer);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [syncAll]);

  // ─── Broadcast Channel — instant override delivery ───────────────────────
  // NOTE: Override is now handled directly by EventoPage via 1s polling.
  // These broadcast handlers only update eventoAtivo state (so components
  // reading eventoAtivo.override_until stay up-to-date) and trigger syncAll.
  // We NO LONGER set alvoAbsoluto or isModoTeste here for override events.
  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) return;

    const broadcastChannel = supabase
      .channel('override-realtime-v1')
      .on('broadcast', { event: 'override_activated' }, (payload: any) => {
        const { override_until, evento_id } = (payload as any).payload ?? {};
        if (!override_until) return;

        const parsedMs = new Date(override_until as string).getTime();
        if (isNaN(parsedMs)) return;
        if (parsedMs <= Date.now() - 10_000) return;

        console.log('[EventSync] ⚡ BROADCAST override_activated — updating eventoAtivo only:', {
          override_until,
          evento_id,
          device: navigator.userAgent.includes('Mobile') ? 'MOBILE' : 'DESKTOP',
        });

        // Only update eventoAtivo so local override detection in EventoPage works.
        // Do NOT set alvoAbsoluto or isModoTeste — EventoPage handles override directly.
        setEventoAtivo(prev => {
          if (!prev) return prev;
          return { ...prev, override_mode: true, override_until: override_until as string };
        });
      })
      .on('broadcast', { event: 'override_cleared' }, (_payload: any) => {
        console.log('[EventSync] ⚡ BROADCAST override_cleared received');
        // Update eventoAtivo to clear override fields immediately
        setEventoAtivo(prev => {
          if (!prev) return prev;
          return { ...prev, override_mode: false, override_until: null };
        });
        // Full sync to recalculate alvoAbsoluto (will compute next 10:00 AM)
        syncAll();
      })
      .subscribe((status) => {
        console.log('[EventSync] Broadcast channel status:', status);
      });

    return () => {
      supabase.removeChannel(broadcastChannel);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Failsafe polling — 1s (GLOBAL fallback for all devices) ─────────────
  useEffect(() => {
    const interval = setInterval(syncAll, 1_000);
    return () => clearInterval(interval);
  }, [syncAll]);

  // ─── Server time auto-sync ───────────────────────────────────────────────
  useEffect(() => {
    const cleanup = startAutoSync(30_000);
    return cleanup;
  }, []);

  // ─── Re-sync on tab visible / window focus (critical for mobile) ─────────
  useEffect(() => {
    // ⛔ Debounce guard: evita rajada de syncAll() quando a aba volta ao foco
    // (visibilitychange + focus + mousemove + click disparam quase simultaneamente)
    let lastSyncTimestamp = 0;
    const MIN_SYNC_INTERVAL = 2000; // mínimo 2s entre syncs disparados por foco

    const debouncedSync = () => {
      const now = Date.now();
      if (now - lastSyncTimestamp < MIN_SYNC_INTERVAL) {
        console.log('[EventSync] ⏭️ Sync ignorado (debounce — último foi há', now - lastSyncTimestamp, 'ms)');
        return;
      }
      lastSyncTimestamp = now;
      syncAll();
    };

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        console.log('[EventSync] 🔄 Tab visible → syncing');
        debouncedSync();
        syncServerTime();
      }
    };
    const handleFocus = () => {
      console.log('[EventSync] 🔄 Window focus → syncing');
      debouncedSync();
      syncServerTime();
    };
    // ── MOBILE EXTRA: touchstart forces immediate re-sync ──────────────────
    const handleTouch = () => debouncedSync();

    // ── DESKTOP EXTRA: mousemove — throttled to 5s ────────────────────────
    let lastMouseSync = 0;
    const handleMouseMove = () => {
      const now = Date.now();
      if (now - lastMouseSync > 5000) {
        lastMouseSync = now;
        debouncedSync();
      }
    };

    // ── DESKTOP EXTRA: click — uses debounce to avoid spam ─────────────────
    const handleClick = () => debouncedSync();

    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('touchstart', handleTouch, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('click', handleClick, { passive: true });

    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('touchstart', handleTouch);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    };
  }, [syncAll]);

  return (
    <EventSyncContext.Provider value={{
      eventoAtivo,
      alvoAbsoluto,
      isModoTeste,
      configCarregada,
      realtimeConnected,
      refresh: syncAll,
    }}>
      {children}
    </EventSyncContext.Provider>
  );
}
